/*
 * flekk-custom.js
 * ---------------------------------------------------------------------------
 * Consolidated custom behaviour for the Flekkfritt theme. Everything in here
 * used to live as inline <script> blocks in layout/theme.liquid, header.liquid,
 * cart-drawer.liquid and under-btn-text.liquid, each polling /cart.js on its
 * own timer (up to ~4 requests per second in total).
 *
 * Now there is ONE cart store (window.FlekkCart) that:
 *   - fetches /cart.js once on load,
 *   - refreshes after any cart mutation (fetch to /cart/add|change|update|clear,
 *     Dawn's pub/sub cart-update event, the legacy cart:updated / cart:change /
 *     ajaxCart.afterCartUpdate events),
 *   - refreshes when the tab becomes visible again,
 *   - falls back to a slow poll (every 15 s) only while the tab is visible,
 *   - de-duplicates concurrent requests and notifies subscribers only when the
 *     cart actually changed.
 *
 * Every feature subscribes to that store instead of polling.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  /* ======================================================================
   * 1. Shared cart store
   * ==================================================================== */
  var FlekkCart = (function () {
    var cache = null;
    var signature = null;
    var inflight = null;
    var subscribers = [];
    var FALLBACK_POLL_MS = 15000;
    var pollTimer = null;

    function cartSignature(cart) {
      // Cheap fingerprint of "did anything relevant change".
      return [
        cart.item_count,
        cart.total_price,
        cart.items.map(function (i) { return i.key + ':' + i.quantity + ':' + i.final_line_price; }).join('|')
      ].join('#');
    }

    function notify() {
      subscribers.forEach(function (fn) {
        try { fn(cache); } catch (e) { console.error('[FlekkCart] subscriber error', e); }
      });
    }

    function refresh(force) {
      if (inflight) return inflight;
      inflight = fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          inflight = null;
          var sig = cartSignature(cart);
          var changed = sig !== signature;
          cache = cart;
          signature = sig;
          if (changed || force) notify();
          return cart;
        })
        .catch(function (err) {
          inflight = null;
          console.error('[FlekkCart] fetch failed', err);
          return cache;
        });
      return inflight;
    }

    function subscribe(fn) {
      subscribers.push(fn);
      if (cache) {
        try { fn(cache); } catch (e) { console.error('[FlekkCart] subscriber error', e); }
      }
      return function () {
        subscribers = subscribers.filter(function (s) { return s !== fn; });
      };
    }

    function get() {
      return cache ? Promise.resolve(cache) : refresh();
    }

    function startPolling() {
      stopPolling();
      pollTimer = setInterval(function () {
        if (document.visibilityState === 'visible') refresh(false);
      }, FALLBACK_POLL_MS);
    }
    function stopPolling() {
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = null;
    }

    /* --- refresh triggers ------------------------------------------------ */

    // a) Any fetch() that mutates the cart. This single wrapper replaces the
    //    three separate wrappers that used to live in header.liquid.
    var originalFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var url = '';
      try {
        url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      } catch (e) {}
      var p = originalFetch.apply(this, args);
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        p.then(function () {
          // Let the legacy listeners know, then refresh the store.
          document.dispatchEvent(new Event('cart:change'));
          setTimeout(function () { refresh(true); }, 50);
        }).catch(function () {});
      }
      return p;
    };

    // b) Dawn pub/sub (product-form.js, cart.js, quick-add-bulk.js ...)
    function hookPubSub() {
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () { refresh(true); });
        return true;
      }
      return false;
    }
    if (!hookPubSub()) document.addEventListener('DOMContentLoaded', hookPubSub);

    // c) Legacy custom events used across the theme.
    ['cart:updated', 'cart:refresh', 'ajaxCart.afterCartUpdate', 'shopify:section:load'].forEach(function (evt) {
      document.addEventListener(evt, function () { refresh(true); });
    });

    // d) Tab visibility.
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') refresh(false);
    });

    // e) Initial load + slow fallback poll.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { refresh(true); });
    } else {
      refresh(true);
    }
    startPolling();

    return { get: get, refresh: refresh, subscribe: subscribe, current: function () { return cache; } };
  })();
  window.FlekkCart = FlekkCart;

  /* Quantity → discount tier (shared by several features) */
  function totalQuantity(cart) {
    return cart.items.reduce(function (sum, item) { return sum + item.quantity; }, 0);
  }
  window.flekkTotalQuantity = totalQuantity;

  var nokFormatter = new Intl.NumberFormat('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  /* ======================================================================
   * 2. Collection "add to cart" forms + cart count bubble
   *    (formerly inline "Bubble new code" in theme.liquid)
   * ==================================================================== */
  function updateCartCount(cart) {
    var count = cart.item_count;
    var cartBubbles = document.querySelectorAll('.cart-count-bubble');

    if (cartBubbles.length > 0) {
      cartBubbles.forEach(function (bubble) {
        var visibleSpan = bubble.querySelector('span[aria-hidden="true"]');
        var hiddenSpan = bubble.querySelector('.visually-hidden');
        if (visibleSpan) visibleSpan.textContent = count;
        if (hiddenSpan) hiddenSpan.textContent = count + ' ' + (count === 1 ? 'item' : 'items');
        bubble.style.display = count > 0 ? 'inline-block' : 'none';
      });
    } else {
      createCartBubble(count);
    }

    document.querySelectorAll('.cart-count, .header__cart-count').forEach(function (icon) {
      icon.textContent = count;
      icon.style.display = count > 0 ? 'inline-block' : 'none';
    });
  }

  function createCartBubble(count) {
    var bubble = document.createElement('div');
    bubble.className = 'cart-count-bubble';
    bubble.innerHTML =
      '<span aria-hidden="true">' + count + '</span>' +
      '<span class="visually-hidden">' + count + ' ' + (count === 1 ? 'item' : 'items') + '</span>';
    bubble.style.display = count > 0 ? 'inline-block' : 'none';

    var cartContainers = ['.site-header__cart', '.header__icon--cart', '.cart-icon', '#cart-icon-bubble', '.header__icons [href*="/cart"]'];
    for (var i = 0; i < cartContainers.length; i++) {
      var container = document.querySelector(cartContainers[i]);
      if (container) {
        var existingBubble = container.querySelector('.cart-count-bubble');
        if (existingBubble) existingBubble.remove();
        container.appendChild(bubble);
        break;
      }
    }
  }

  function refreshCartDrawer() {
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.fetchContents === 'function') {
      cartDrawer.fetchContents();
    } else {
      fetch('/cart?section_id=cart-drawer')
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newCartDrawer = doc.querySelector('cart-drawer');
          var existingCartDrawer = document.querySelector('cart-drawer');
          if (newCartDrawer && existingCartDrawer) existingCartDrawer.innerHTML = newCartDrawer.innerHTML;
        })
        .catch(function (error) { console.error('Cart drawer refresh failed:', error); });
    }
  }

  onReady(function () {
    document.querySelectorAll('.collection-add-to-cart-form').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var formData = new FormData(this);
        var submitButton = this.querySelector('.add-to-cart-btn');

        fetch('/cart/add.js', { method: 'POST', body: formData, headers: { Accept: 'application/json' } })
          .then(function (response) {
            if (!response.ok) throw new Error('Add to cart failed');
            return response.json();
          })
          .then(function (data) {
            if (submitButton) {
              submitButton.textContent = 'Lagt til';
              submitButton.classList.add('added');
            }
            refreshCartDrawer();
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));
          })
          .catch(function (error) { console.error('Error:', error); });
        return false;
      });
    });

    document.addEventListener('cart:updated', refreshCartDrawer);
  });

  FlekkCart.subscribe(updateCartCount);
  // Backwards compatibility: snippets/add-to-cart-btn.liquid calls a global
  // updateCartCount() with no arguments after adding to the cart.
  window.updateCartCount = function () { return FlekkCart.refresh(true); };

  /* ======================================================================
   * 3. Quantity-based discount shown on price elements
   *    (formerly checkAndUpdatePrices polling /cart.js every second)
   * ==================================================================== */
  var PRICE_SELECTOR = [
    'span.price-item.price-item--regular',
    'span.price-item.price-item--sale.price-item--last',
    'p.modal-pricing',
    'product-recommendations.related-products span.sale-price',
    'p.cart-item-price',
    '.sticky-price-product-custom'
  ].join(', ');

  var currentDiscount = 0;

  function discountForQuantity(qty) {
    if (qty >= 5) return 0.20;
    if (qty >= 4) return 0.15;
    if (qty >= 3) return 0.10;
    if (qty >= 2) return 0.05;
    return 0;
  }

  function applyDiscountToPrices() {
    var discount = currentDiscount;
    document.querySelectorAll(PRICE_SELECTOR).forEach(function (el) {
      var applied = el.dataset.discountApplied;
      if (applied !== undefined && parseFloat(applied) === discount) {
        // Already up to date, unless another script replaced our markup with plain text.
        if (discount === 0 || el.querySelector('span')) return;
        delete el.dataset.originalPrice;
      }

      var rawText = el.textContent.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
      var originalPrice = parseFloat(el.dataset.originalPrice || rawText);
      if (isNaN(originalPrice)) return;

      if (discount > 0) {
        if (!el.dataset.originalPrice) el.dataset.originalPrice = originalPrice;
        var discounted = originalPrice * (1 - discount);
        el.innerHTML =
          '<span style="text-decoration: line-through; color: #5FB3C4; margin-right: 6px;">' +
            nokFormatter.format(originalPrice) + ' NOK' +
          '</span>' +
          '<span style="color: #00514E; font-weight: bold;">' +
            nokFormatter.format(discounted) + ' NOK' +
          '</span>';
      } else if (el.dataset.originalPrice) {
        el.textContent = nokFormatter.format(parseFloat(el.dataset.originalPrice)) + ' NOK';
        delete el.dataset.originalPrice;
      }
      el.dataset.discountApplied = discount;
    });
  }

  FlekkCart.subscribe(function (cart) {
    currentDiscount = discountForQuantity(totalQuantity(cart));
    applyDiscountToPrices();
  });

  // Price elements are re-rendered by Dawn (variant change, cart drawer, related
  // products loading later). Watch for new nodes instead of polling.
  var priceObserverTimer = null;
  onReady(function () {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length || mutations[i].type === 'characterData') {
          clearTimeout(priceObserverTimer);
          priceObserverTimer = setTimeout(applyDiscountToPrices, 150);
          return;
        }
      }
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
  });

  /* ======================================================================
   * 4. Wash-price badges (product page + cards)
   * ==================================================================== */
  function pickWashPrice(el) {
    var wash5 = el.dataset.wash5;
    var wash4 = el.dataset.wash4;
    return wash5 && wash5.trim() !== '' ? wash5 : wash4 && wash4.trim() !== '' ? wash4 : null;
  }

  function setWashPrice() {
    var wrapper = document.querySelector('.product-wash-price-badge');
    if (!wrapper) return;
    var washPrice = pickWashPrice(wrapper);
    if (!washPrice) return;

    document.querySelectorAll('.wash-price-badge, .wash-price-text-2').forEach(function (el) { el.remove(); });

    var priceContainer = document.querySelector('.price__container');
    if (priceContainer) {
      var badge1 = document.createElement('li');
      badge1.className = 'wash-price-badge';
      badge1.textContent = 'Pris per vask ned til ' + washPrice;
      priceContainer.appendChild(badge1);

      var badge3 = document.createElement('div');
      badge3.className = 'wash-price-text-2';
      badge3.textContent = 'Pris per vask ned til ' + washPrice;
      priceContainer.insertAdjacentElement('afterend', badge3);
    }
  }

  function addWashBadges() {
    document.querySelectorAll('.card__media').forEach(function (cardMedia) {
      var oldBadge = cardMedia.querySelector('.wash-price-badge');
      if (oldBadge) oldBadge.remove();
      var washPrice = pickWashPrice(cardMedia);
      if (!washPrice) return;
      if (cardMedia.querySelector('.wash-price-badge-collection')) return;

      var badge = document.createElement('div');
      badge.className = 'wash-price-badge-collection';
      badge.textContent = 'Pris per vask ned til ' + washPrice;
      cardMedia.style.position = 'relative';
      cardMedia.appendChild(badge);
    });
  }

  function addFeaturedImageBadgeMobile() {
    if (window.innerWidth > 749) {
      document.querySelectorAll('.wash-price-image-badge').forEach(function (el) { el.remove(); });
      return;
    }
    var wrapper = document.querySelector('.product-wash-price-badge');
    if (!wrapper) return;
    var washPrice = pickWashPrice(wrapper);
    if (!washPrice) return;

    document.querySelectorAll('.wash-price-image-badge').forEach(function (el) { el.remove(); });

    var mediaContainer =
      document.querySelector('.product__media-item.is-active .media') ||
      document.querySelector('slider-component .product__media-item .media') ||
      document.querySelector('.product__media-item .media') ||
      document.querySelector('.product__media-wrapper .media');
    if (!mediaContainer) return;

    mediaContainer.style.position = 'relative';
    var badge = document.createElement('div');
    badge.className = 'wash-price-image-badge';
    badge.textContent = 'Pris per vask ned til ' + washPrice;
    mediaContainer.appendChild(badge);
  }

  onReady(function () {
    setWashPrice();
    addWashBadges();
    addFeaturedImageBadgeMobile();
  });
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(addFeaturedImageBadgeMobile, 150);
  }, { passive: true });

  /* ======================================================================
   * 5. Diagonal badges (data comes from window.diagonalBadges set in Liquid)
   * ==================================================================== */
  (function () {
    function getHandleFromUrl(href) {
      if (!href) return null;
      var m = href.match(/\/products\/([a-z0-9\-_]+)/i);
      return m ? m[1] : null;
    }

    function makeBadge(text) {
      var w = document.createElement('div');
      w.className = 'diagonal-badge-wrapper';
      var s = document.createElement('span');
      s.className = 'diagonal-badge';
      s.textContent = text;
      w.appendChild(s);
      return w;
    }

    function applyBadges() {
      var badges = window.diagonalBadges || {};

      document.querySelectorAll('.card-wrapper').forEach(function (card) {
        if (card.querySelector('.diagonal-badge-wrapper')) return;
        var link = card.querySelector('a[href*="/products/"]');
        var handle = getHandleFromUrl(link ? link.getAttribute('href') : null);
        if (!handle) return;
        var text = badges[handle];
        if (!text || !String(text).trim()) return;
        (card.querySelector('.card__inner') || card).appendChild(makeBadge(String(text).trim()));
      });

      document.querySelectorAll('a.related-product-image[href*="/products/"]').forEach(function (link) {
        if (link.querySelector('.diagonal-badge-wrapper')) return;
        var handle = getHandleFromUrl(link.getAttribute('href'));
        if (!handle) return;
        var text = badges[handle];
        if (!text || !String(text).trim()) return;
        link.appendChild(makeBadge(String(text).trim()));
      });

      if (typeof window.currentProductBadge !== 'undefined') {
        var t = window.currentProductBadge;
        if (t && String(t).trim()) {
          document.querySelectorAll('media-gallery').forEach(function (g) {
            if (g.querySelector('.diagonal-badge-wrapper')) return;
            g.appendChild(makeBadge(String(t).trim()));
          });
        }
      }
    }

    onReady(function () {
      applyBadges();
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          for (var j = 0; j < muts[i].addedNodes.length; j++) {
            var n = muts[i].addedNodes[j];
            if (n.nodeType === 1 && (
              n.matches('.card-wrapper, .related-product-image') ||
              n.querySelector('.card-wrapper, media-gallery, .related-product-image')
            )) {
              applyBadges();
              return;
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    });
  })();

  /* ======================================================================
   * 6. Custom cart-drawer rendering (formerly "CArt Drawer Refresh Code")
   *    Re-renders the drawer table from the cart store instead of doing its
   *    own /cart.js request. Re-runs whenever the cart changes.
   * ==================================================================== */
  function renderCartDrawer(cart) {
    var tbody = document.querySelector('#CartDrawer-CartItems .cart-items tbody');
    var totalEl = document.querySelector('.totals__total-value');
    if (!tbody) return;

    var fmt = function (cents, digits) {
      return (cents / 100).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: digits === undefined ? 3 : digits });
    };

    var rows = cart.items.map(function (item, index) {
      var linePrice = fmt(item.final_line_price, 2);
      var originalPrice = fmt(item.original_price);
      var originalTotalPrice = ((item.original_price / 100) * item.quantity).toLocaleString('da-DK', { minimumFractionDigits: 2 });

      var discountMarkup = item.discounts.length > 0
        ? '<ul class="discounts list-unstyled" role="list" aria-label="Discounts">' +
            item.discounts.map(function (discount) {
              return '<li class="discounts__discount">' +
                '<svg class="icon icon-discount" viewBox="0 0 12 12"><path fill="currentColor" fill-rule="evenodd" d="M7 0h3a2 2 0 0 1 2 2v3a1 1 0 0 1-.3.7l-6 6a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4l6-6A1 1 0 0 1 7 0m2 2a1 1 0 1 0 2 0 1 1 0 0 0-2 0" clip-rule="evenodd"></path></svg>' +
                discount.title + '</li>';
            }).join('') +
          '</ul>'
        : '';

      var options = item.options_with_values.map(function (opt) {
        return '<div class="product-option"><dt>' + opt.name + ':</dt><dd>' + opt.value + '</dd></div>';
      }).join('');

      return '' +
        '<tr class="cart-item" id="CartDrawer-Item-' + (index + 1) + '" role="row">' +
          '<td class="cart-item__media" role="cell">' +
            '<a href="' + item.url + '" class="cart-item__link" tabindex="-1" aria-hidden="true"></a>' +
            '<img class="cart-item__image" src="' + item.image + '" alt="' + item.title + '" loading="lazy" width="150" height="150">' +
          '</td>' +
          '<td class="cart-item__details" role="cell">' +
            '<a href="' + item.url + '" class="cart-item__name h4 break">' + item.title + '</a>' +
            '<div class="product-option">' + originalPrice + ' kr</div>' +
            options + discountMarkup +
          '</td>' +
          '<td class="cart-item__totals right" role="cell">' +
            '<div class="cart-item__price-wrapper">' +
              '<s class="cart-item__old-price price price--end two">' + originalTotalPrice + ' kr</s>' +
              '<span class="price price--end">' + linePrice + ' kr</span>' +
            '</div>' +
          '</td>' +
          '<td class="cart-item__quantity" role="cell">' +
            '<div class="cart-item__quantity-wrapper quantity-popover-wrapper"><div class="quantity-popover-container">' +
              '<div class="quantity cart-quantity">' +
                '<button type="button" class="quantity__button" data-key="' + item.key + '" data-action="decrease">-</button>' +
                '<input class="quantity__input" type="number" value="' + item.quantity + '" min="1" readonly>' +
                '<button type="button" class="quantity__button" data-key="' + item.key + '" data-action="increase">+</button>' +
              '</div>' +
              '<cart-remove-button id="CartDrawer-Remove-' + (index + 1) + '" data-index="' + (index + 1) + '">' +
                '<button type="button" class="button button--tertiary cart-remove-button" aria-label="Remove ' + item.title + '" data-variant-id="' + item.variant_id + '" data-key="' + item.key + '">' +
                  '<span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" class="icon icon-remove" viewBox="0 0 16 16"><path fill="currentColor" d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5 0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1M6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02m4.84 11.52h-7.5V4h7.5z"/><path fill="currentColor" d="M6.55 5.25a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5m2.9 0a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5"/></svg></span>' +
                '</button>' +
              '</cart-remove-button>' +
            '</div></div>' +
          '</td>' +
        '</tr>';
    }).join('');

    tbody.innerHTML = rows;

    if (totalEl) {
      totalEl.textContent = (cart.total_price / 100).toLocaleString('da-DK', { minimumFractionDigits: 2 }) + ' kr';
    }
  }

  // Event delegation: one listener for +/-/remove instead of re-binding on
  // every render (which also used to leak listeners across renders).
  onReady(function () {
    var drawer = document.querySelector('#CartDrawer-CartItems');
    if (!drawer) return;

    drawer.addEventListener('click', function (e) {
      var qtyBtn = e.target.closest('.quantity__button[data-key]');
      var removeBtn = e.target.closest('.cart-remove-button[data-key]');

      if (qtyBtn) {
        var input = qtyBtn.parentElement.querySelector('.quantity__input');
        var currentQty = parseInt(input.value, 10) || 1;
        var newQty = qtyBtn.dataset.action === 'increase' ? currentQty + 1 : currentQty - 1;
        if (newQty < 1) newQty = 1;
        changeCart(qtyBtn.dataset.key, newQty);
      } else if (removeBtn) {
        changeCart(removeBtn.dataset.key, 0);
      }
    });

    function changeCart(key, quantity) {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then(function () { return FlekkCart.refresh(true); })
        .then(function (cart) { if (cart) renderCartDrawer(cart); })
        .catch(function (err) { console.error('Cart change failed', err); });
    }
  });

  // As before: the custom markup is rendered once on page load (Dawn keeps
  // re-rendering the drawer itself after add-to-cart) and after +/-/remove.
  var drawerRendered = false;
  FlekkCart.subscribe(function (cart) {
    if (drawerRendered) return;
    drawerRendered = true;
    renderCartDrawer(cart);
  });

  /* ======================================================================
   * 7. Shopify cookie banner overlay + consent → gtag bridge
   * ==================================================================== */
  onReady(function () {
    setTimeout(function () {
      var modal = document.querySelector('#shopify-pc__banner');
      if (!modal) return;
      var overlay = document.createElement('div');
      overlay.id = 'shopify-pc-overlay';
      document.body.appendChild(overlay);

      var sync = function () { overlay.style.display = modal.style.display !== 'none' ? 'block' : 'none'; };
      sync();
      new MutationObserver(sync).observe(modal, { attributes: true, attributeFilter: ['style'] });
    }, 2000);
  });

  function allConsentGranted() {
    if (typeof window.gtag !== 'function') return;
    window.gtag('consent', 'update', {
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      ad_storage: 'granted',
      analytics_storage: 'granted',
      functionality_storage: 'granted',
      security_storage: 'granted',
      personalization_storage: 'granted'
    });
  }
  window.allConsentGranted = allConsentGranted;

  document.addEventListener('click', function (event) {
    if (event.target.closest('button#shopify-pc__banner__btn-accept') ||
        (event.target && event.target.id === 'shopify-pc__prefs__header-accept')) {
      allConsentGranted();
    }
  });
})();
